import {
	type DeliveryMethodKind,
	VENDOR_AGGREGATION_GROUP,
	type VendorListItem,
} from "@dransay/api/models";
import { useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useFetcher } from "react-router";
import { getAggregatedVendorOptions } from "~/features/products/vendor-options";

interface UseVendorProps {
	vendorId?: string;
	deliveryMethodKind?: DeliveryMethodKind;
}

type UseVendorReturn = {
	vendor?: VendorListItem;
	isLoading: boolean;
};

const AGGREGATION_GROUP_IDS = new Set<string>(
	Object.values(VENDOR_AGGREGATION_GROUP),
);

export function useVendor({
	vendorId,
	deliveryMethodKind,
}: UseVendorProps): UseVendorReturn {
	const fetcher = useFetcher<{ items: VendorListItem[] }>();
	const { t } = useTranslation("product");

	const isAggregationGroup = vendorId
		? AGGREGATION_GROUP_IDS.has(vendorId)
		: false;

	const loadVendor = () => {
		if (!vendorId || isAggregationGroup) return;
		const params = new URLSearchParams({ id: vendorId });
		if (deliveryMethodKind) {
			params.append("deliveryMethodKind", deliveryMethodKind);
		}

		fetcher.load(`/api/vendors?${params}`);
	};

	// biome-ignore lint/correctness/useExhaustiveDependencies:
	useEffect(loadVendor, [vendorId, deliveryMethodKind]);

	const [fetchedVendor] = fetcher.data?.items ?? [];

	const aggregatedVendorOptions = getAggregatedVendorOptions(
		deliveryMethodKind,
		t,
	);

	const vendor =
		aggregatedVendorOptions.find((option) => option.id === vendorId) ??
		fetchedVendor;

	return {
		vendor,
		isLoading:
			!isAggregationGroup && fetcher.state === "loading" && !fetcher.data,
	};
}
